import { ArrowRight, Download, MonitorPlay, Plus, QrCode, Smartphone } from 'lucide-react'
import { BackLink, ButtonLink } from '@/components/play'
import { authLink } from '@/components/onboarding/authContinuation'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { SitePage } from '@/components/SitePage'

const steps = [
  {
    icon: Plus,
    title: 'Create an event',
    description: 'Start with a name. We’ll take it from there.',
    image: '/walkthrough/events-dashboard.png',
    imageAlt: 'Events dashboard showing live events',
  },
  {
    icon: MonitorPlay,
    title: 'Add your cameras',
    description: 'Add a camera for each spot on the course: the start, a climb, the finish. A camera can be a phone, a field camera or supported glasses.',
    image: '/walkthrough/event-detail-live.png',
    imageAlt: 'Event page with 5 live camera feeds',
  },
  {
    icon: QrCode,
    title: 'Share the camera invite',
    description: 'Each camera gets its own QR code and invite link. Share it with the person on that camera. They don’t need an account.',
    image: '/walkthrough/invite-qr-mobile.jpg',
    imageAlt: 'Camera invite with a QR code',
    phone: true,
  },
  {
    icon: Smartphone,
    title: 'Scan and go live',
    description: 'Open the StreamBridge camera app, scan the QR code and tap Join. The app adjusts quality to the connection and reconnects on its own.',
    image: '/walkthrough/app-join.jpg',
    imageAlt: 'StreamBridge camera app join screen',
    phone: true,
  },
  {
    icon: Download,
    title: 'Download OBS setup',
    description: 'OBS setup imports every camera as a scene, ready to mix your show. Add a live show to bring the finished show back to your event.',
    image: '/walkthrough/obs-all-cameras.jpg',
    imageAlt: 'OBS Studio with all 5 StreamBridge cameras imported',
  },
]

/** How it works (/how-it-works): the five steps from a name to OBS, with product screenshots. */
export function WalkthroughPage() {
  useDocumentTitle('How it works · StreamBridge')
  return (
    <SitePage mainId="how-it-works-main">
      <div className="mx-auto max-w-6xl px-6 pt-6">
        <BackLink to="/">StreamBridge</BackLink>
      </div>

      <section className="px-6 pb-12 pt-12 text-center">
        <h1 className="text-4xl font-black text-neutral-10 sm:text-5xl">How StreamBridge works</h1>
        <p className="mx-auto mt-4 max-w-lg text-lg text-neutral-7">From a name to cameras on the course, in five steps.</p>
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-24" aria-label="Steps">
        <ol className="space-y-32" role="list">
          {steps.map((step, index) => (
            <li
              key={step.title}
              className={`flex flex-col items-center gap-12 lg:flex-row lg:gap-16 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
            >
              <div className="flex-1">
                <div className="mb-4 flex items-center gap-3 text-neutral-7" aria-hidden="true">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-neutral-2 text-neutral-10">
                    <step.icon className="h-5 w-5" />
                  </span>
                  <span className="font-data text-xl font-black tabular-nums">{String(index + 1).padStart(2, '0')}</span>
                </div>
                <h2 className="text-3xl font-black text-neutral-10 sm:text-4xl">{step.title}</h2>
                <p className="mt-4 text-lg text-neutral-7">{step.description}</p>
              </div>

              <div className={`flex-1 ${step.phone ? 'flex justify-center' : ''}`}>
                <div className={`overflow-hidden rounded-2xl bg-neutral-2 ${step.phone ? 'max-w-[280px]' : ''}`}>
                  <img src={step.image} alt={step.imageAlt} className="block h-auto w-full" loading="lazy" />
                </div>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-24 text-center" aria-labelledby="walkthrough-multiview-title">
        <h2 id="walkthrough-multiview-title" className="text-3xl font-black text-neutral-10 sm:text-4xl">See every camera at once</h2>
        <p className="mx-auto mt-4 max-w-lg text-lg text-neutral-7">
          Multiview shows all your cameras together. Choose how many columns, or let it fit them for you. Expand any
          camera to focus on it.
        </p>
        <div className="mt-10 overflow-hidden rounded-2xl bg-neutral-2">
          <img src="/walkthrough/multiview-focused.jpg" alt="Multiview with a focused camera and a sidebar" className="block h-auto w-full" loading="lazy" />
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-24">
        <div className="overflow-hidden rounded-2xl bg-neutral-2">
          <img
            src="/walkthrough/app-scan-qr.jpg"
            alt="Scanning a camera invite QR code with the StreamBridge camera app while cameras show on screen"
            className="block h-auto w-full"
            loading="lazy"
          />
        </div>
      </section>

      <section className="border-t border-neutral-4 px-6 py-20 text-center" aria-labelledby="walkthrough-cta-title">
        <h2 id="walkthrough-cta-title" className="text-3xl font-black text-neutral-10 sm:text-4xl">Bring your event to life.</h2>
        <p className="mx-auto mt-4 max-w-md text-lg text-neutral-7">Create an event and bring your cameras.</p>
        <ButtonLink to={authLink('/signup', '/app?create=1')} className="mt-8">
          Create event
          <ArrowRight aria-hidden="true" />
        </ButtonLink>
      </section>
    </SitePage>
  )
}
